import { useRef, useState } from "react";
import { ArrowDownToLine, BarChart3, FileUp, Loader2 } from "lucide-react";
import { monthLabel, summarizeTourVotes } from "@/lib/tourVoteInsights";

function safeCsv(value) {
  const text = String(value == null ? "" : value);
  const escaped = /^[=+\-@\t\r]/.test(text) ? "'" + text : text;
  return '"' + escaped.replace(/"/g, '""') + '"';
}

export default function TourVoteInsights() {
  const upload = useRef(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function readFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      if (file.size > 5_000_000) throw new Error("Choose a CSV under 5 MB.");
      const summary = summarizeTourVotes(await file.text());
      setReport(summary);
    } catch (err) {
      setReport(null);
      setError(err.message || "Could not read the vote CSV.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  function exportSummary() {
    if (!report) return;
    const rows = [["City", "Votes", "Likely to attend", "Early-access opt-ins", "Top month", "Top month votes", "Top weekend", "Top time"]];
    for (const row of report.cities) {
      rows.push([row.city, row.votes, row.likely, row.earlyAccess, row.topMonth?.value || "", row.topMonth?.count || 0, row.topWeekend?.value || "", row.topTime?.value || ""]);
    }
    const csv = rows.map((row) => row.map(safeCsv).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "pole-tour-vote-summary-2027.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="mt-8 rounded-3xl border border-white/10 bg-[#0d0d0d] p-5 sm:p-8" aria-labelledby="tour-vote-report">
      <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="flex items-center gap-2 font-accent text-[11px] tracking-[.2em] text-[#39E75F]"><BarChart3 size={17} /> PRIVATE TOUR RESEARCH</p>
          <h2 id="tour-vote-report" className="mt-2 font-display text-3xl sm:text-4xl">Audience date votes</h2>
          <p className="mt-3 max-w-xl text-sm leading-6 text-white/55">Compare submissions by city and month using the vote CSV from Netlify Forms. These are preference submissions, not verified unique attendees. Files are analyzed locally in this browser and are not uploaded elsewhere.</p>
        </div>
        <div className="flex shrink-0 flex-col gap-2">
          <input ref={upload} type="file" accept=".csv,text/csv" className="sr-only" aria-label="Choose Pole & Polaroids vote CSV" onChange={readFile} />
          <button type="button" disabled={busy} onClick={() => upload.current?.click()} className="flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#39E75F] px-6 text-xs font-accent tracking-widest text-black disabled:opacity-50">
            {busy ? <Loader2 size={17} className="animate-spin" /> : <FileUp size={17} />} IMPORT VOTE CSV
          </button>
          {report && <button type="button" onClick={exportSummary} className="flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/20 px-5 text-xs font-accent tracking-widest hover:border-[#39E75F]"><ArrowDownToLine size={16} /> EXPORT SUMMARY</button>}
        </div>
      </div>
      <p className="mt-4 text-xs leading-6 text-white/45">Export from <a href="https://app.netlify.com/projects/pole-and-polaroids/forms" target="_blank" rel="noreferrer" className="text-[#A3FFB1] underline underline-offset-4">Netlify → Forms</a>, select <strong>pole-tour-date-vote-2027</strong>, then download the CSV. This report is based on the imported file, not live synchronized data.</p>
      {error && <p role="alert" className="mt-4 rounded-xl border border-[#E32636]/40 bg-[#E32636]/10 p-4 text-sm text-red-200">{error}</p>}
      {report && <>
        <div className="mt-7 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[[report.total, "VALID VOTES"], [report.cities.length, "CITIES"], [report.earlyAccess, "EMAIL OPT-INS"], [report.omitted, "ROWS OMITTED"]].map(([value, label]) => (
            <div key={label} className="rounded-2xl border border-white/10 bg-black/30 p-4"><div className="font-display text-4xl">{value}</div><div className="mt-1 text-[10px] font-accent tracking-[.14em] text-white/50">{label}</div></div>
          ))}
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.3fr_.7fr]">
          <div className="min-w-0 overflow-hidden rounded-2xl border border-white/10">
            <div className="bg-white/[.04] p-4 font-accent text-xs tracking-widest">CITY DEMAND · MOST VOTES FIRST</div>
            {report.cities.map((city, index) => (
              <div key={city.city} className="grid grid-cols-[1.3rem_1fr_auto] items-start gap-3 border-t border-white/10 p-4">
                <span className="pt-1 text-xs text-white/35">{index + 1}</span>
                <div className="min-w-0">
                  <p className="font-accent tracking-wide">{city.city}</p>
                  <p className="mt-1 text-xs leading-5 text-white/55">{city.topMonth ? monthLabel(city.topMonth.value) + " (" + city.topMonth.count + ")" : "No month"} · {city.likely} likely attendees</p>
                  {city.topWeekend && <p className="mt-1 break-words text-[11px] text-white/40">Top requested weekend: {city.topWeekend.value} ({city.topWeekend.count})</p>}
                </div>
                <strong className="font-display text-3xl">{city.votes}</strong>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-white/10">
            <div className="bg-white/[.04] p-4 font-accent text-xs tracking-widest">POPULAR MONTHS</div>
            {report.months.map((month) => (
              <div key={month.month} className="border-t border-white/10 px-4 py-3">
                <div className="flex justify-between gap-3 text-sm"><span>{month.label}</span><span className="font-semibold">{month.votes}</span></div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#E32636]" style={{ width: String(report.total ? (month.votes / report.total) * 100 : 0) + "%" }} /></div>
              </div>
            ))}
          </div>
        </div>
      </>}
      {!report && !error && <p className="mt-8 rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-white/45">Import a vote CSV to view city rankings, preferred months and early-access demand. No results are displayed until real submissions are provided.</p>}
    </section>
  );
}
